import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Check, MessageCircle, Share2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/me/referrals").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;

  // Prefer the canonical URL returned by the backend (built from PUBLIC_SITE_URL
  // so shares always point to production, never the preview host). Fall back
  // to same-origin for old backend versions.
  const shareUrl = data.share_url || `${window.location.origin}/register?ref=${data.code}`;
  const shareMsg = `Hey! I'm using To-Let Rentals — a zero-brokerage rentals platform for India. Sign up with my code ${data.code} and we both get ${data.reward_per_referral} free contact reveals: ${shareUrl}`;

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  const shareWhatsapp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
    window.open(url, "_blank", "noopener");
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "To-Let Rentals", text: shareMsg, url: shareUrl });
      } catch {}
    } else {
      copy(shareMsg, "Message copied — paste anywhere!");
    }
  };

  return (
    <div className="tlr-card p-6 md:p-8" data-testid="referral-card">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#FBF6EC] flex items-center justify-center text-[#7A1E2A]">
            <Gift size={22} />
          </div>
          <div>
            <p className="label-eyebrow text-gray-500">Refer & Earn</p>
            <h2 className="font-display text-2xl brand-forest">Invite friends, get free reveals</h2>
            <p className="text-sm text-gray-600 mt-1">
              Both of you get <strong>{data.reward_per_referral} free contact reveals</strong> when someone signs up with your code.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Stat label="Signups" value={data.count} icon={<Users size={14} />} />
          <Stat label="Rewards" value={`+${data.rewards_earned}`} accent />
        </div>
      </div>

      {/* Code + copy */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2 block">Your code</label>
          <button
            onClick={() => copy(data.code, "Code copied!")}
            data-testid="ref-code-copy"
            className="w-full h-14 px-4 rounded-xl border-2 border-dashed border-[#C9A24A] bg-[#FBF6EC]/50 flex items-center justify-between font-mono font-bold text-xl text-[#7A1E2A] tracking-wide hover:bg-[#FBF6EC] transition-colors"
          >
            <span>{data.code}</span>
            {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} className="text-[#7A1E2A]/60" />}
          </button>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2 block">Share link</label>
          <button
            onClick={() => copy(shareUrl, "Link copied!")}
            data-testid="ref-link-copy"
            className="w-full h-14 px-4 rounded-xl border border-[#EFE3D0] bg-white flex items-center justify-between text-sm text-gray-700 hover:border-[#7A1E2A]/40 transition-colors truncate"
          >
            <span className="truncate">{shareUrl}</span>
            <Copy size={16} className="text-gray-400 shrink-0 ml-2" />
          </button>
        </div>
      </div>

      {/* Share actions */}
      <div className="flex flex-wrap gap-2">
        <Button data-testid="ref-share-whatsapp" onClick={shareWhatsapp} className="bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full h-10">
          <MessageCircle size={15} className="mr-1.5" /> WhatsApp
        </Button>
        <Button data-testid="ref-share-native" onClick={shareNative} variant="outline" className="rounded-full h-10 border-[#7A1E2A]/30 text-[#7A1E2A] hover:bg-[#7A1E2A]/8">
          <Share2 size={15} className="mr-1.5" /> Share
        </Button>
      </div>

      {/* Referrals list */}
      {data.items && data.items.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[#EFE3D0]">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">Recent signups</p>
          <ul className="space-y-2 max-h-40 overflow-y-auto" data-testid="ref-list">
            {data.items.map((r, i) => (
              <li key={i} className="flex items-center justify-between text-sm py-1.5">
                <span className="truncate text-gray-700">{maskEmail(r.referee_email)}</span>
                <span className="text-xs text-emerald-700 font-semibold shrink-0">+{r.reward_contacts} reveals</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, icon }) {
  return (
    <div className="text-right">
      <div className={`text-2xl font-display ${accent ? "text-[#C9A24A]" : "text-[#7A1E2A]"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center justify-end gap-1">
        {icon} {label}
      </div>
    </div>
  );
}

function maskEmail(email) {
  if (!email) return "—";
  const [name, domain] = email.split("@");
  if (!domain) return email;
  const shown = name.slice(0, 2);
  return `${shown}${"•".repeat(Math.max(1, name.length - 2))}@${domain}`;
}
